import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Upload, Download, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const BulkImportModal = ({ isOpen, onClose, onImportComplete }) => {
  const { toast } = useToast();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [sendEmails, setSendEmails] = useState(false);
  const [results, setResults] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setResults(null);
    } else {
      toast({
        title: "Ugyldig fil",
        description: "Vennligst velg en CSV-fil",
        variant: "destructive"
      });
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast({
        title: "Ingen fil valgt",
        description: "Vennligst velg en CSV-fil først",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      // Read file content
      const fileContent = await file.text();
      
      // Send to backend
      const response = await axios.post(
        `${API}/bookings/import/csv?send_emails=${sendEmails}`,
        fileContent,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'text/csv'
          }
        }
      );

      setResults(response.data);
      
      if (response.data.success > 0) {
        toast({
          title: "Import Fullført! ✅",
          description: `${response.data.success} av ${response.data.total} bookinger importert`
        });
        
        if (onImportComplete) {
          onImportComplete();
        }
      }
    } catch (error) {
      console.error('Error importing bookings:', error);
      toast({
        title: "Import Feilet",
        description: error.response?.data?.detail || "Kunne ikke importere bookinger",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const template = `customer_name,customer_email,customer_phone,date,time_slot,slot_number,vehicle_type,notes,status
Ole Hansen,ole@example.com,12345678,2025-12-01,09:00,1,personbil,Første kontroll,confirmed
Maria Larsen,maria@example.com,87654321,2025-12-01,10:00,2,tungbil,EU-kontroll,confirmed
John Andersen,john@example.com,11223344,2025-12-02,11:00,3,tilhenger,Service,confirmed`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'booking_import_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleClose = () => {
    setFile(null);
    setResults(null);
    setSendEmails(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center space-x-2">
            <Upload className="w-6 h-6 text-red-400" />
            <span>Bulk Import Bookinger</span>
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Last opp en CSV-fil for å importere flere bookinger samtidig
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Instructions */}
          <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-4">
            <h3 className="text-blue-400 font-semibold mb-2 flex items-center">
              <AlertCircle className="w-4 h-4 mr-2" />
              Instruksjoner
            </h3>
            <ul className="text-sm text-gray-300 space-y-1 list-disc list-inside">
              <li>Last ned mal-filen under for å se riktig format</li>
              <li>CSV må inneholde: customer_name, customer_email, customer_phone, date, time_slot, slot_number, vehicle_type</li>
              <li>Valgfrie felt: notes, status (standard: confirmed)</li>
              <li>Dato format: YYYY-MM-DD (f.eks. 2025-12-01)</li>
              <li>Tidspunkt format: HH:MM (f.eks. 09:00)</li>
              <li>Vehicle type: personbil, varebil, tungbil, eller tilhenger</li>
            </ul>
          </div>

          {/* Download Template */}
          <div className="flex justify-center">
            <Button
              onClick={downloadTemplate}
              variant="outline"
              className="border-green-600 text-green-400 hover:bg-green-600/20"
            >
              <Download className="w-4 h-4 mr-2" />
              Last ned CSV-mal
            </Button>
          </div>

          {/* File Upload */}
          <div className="space-y-3">
            <Label className="text-gray-300">Velg CSV-fil</Label>
            <div className="flex items-center space-x-3">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="flex-1 text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-red-600 file:text-white hover:file:bg-red-700 cursor-pointer"
              />
              {file && (
                <Badge className="bg-green-600/20 text-green-400 border-green-600/50">
                  <FileText className="w-3 h-3 mr-1" />
                  {file.name}
                </Badge>
              )}
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="send_emails"
              checked={sendEmails}
              onChange={(e) => setSendEmails(e.target.checked)}
              className="w-4 h-4 text-red-600 bg-gray-900 border-gray-700 rounded focus:ring-red-600"
            />
            <Label htmlFor="send_emails" className="text-gray-300 cursor-pointer">
              Send bekreftelse-email til kundene
            </Label>
          </div>

          {/* Results */}
          {results && (
            <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
              <h3 className="text-white font-semibold">Import Resultat</h3>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-900/20 rounded p-3 text-center">
                  <div className="text-2xl font-bold text-blue-400">{results.total}</div>
                  <div className="text-xs text-gray-400">Totalt</div>
                </div>
                <div className="bg-green-900/20 rounded p-3 text-center">
                  <div className="text-2xl font-bold text-green-400 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 mr-1" />
                    {results.success}
                  </div>
                  <div className="text-xs text-gray-400">Suksess</div>
                </div>
                <div className="bg-red-900/20 rounded p-3 text-center">
                  <div className="text-2xl font-bold text-red-400 flex items-center justify-center">
                    <XCircle className="w-5 h-5 mr-1" />
                    {results.failed}
                  </div>
                  <div className="text-xs text-gray-400">Feilet</div>
                </div>
              </div>

              {results.errors && results.errors.length > 0 && (
                <div className="bg-red-900/20 border border-red-600/30 rounded p-3 max-h-40 overflow-y-auto">
                  <h4 className="text-red-400 font-semibold text-sm mb-2">Feil:</h4>
                  <ul className="text-xs text-gray-300 space-y-1">
                    {results.errors.map((error, idx) => (
                      <li key={idx}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {results.created_bookings && results.created_bookings.length > 0 && (
                <div className="bg-green-900/20 border border-green-600/30 rounded p-3 max-h-40 overflow-y-auto">
                  <h4 className="text-green-400 font-semibold text-sm mb-2">
                    Opprettede bookinger ({results.created_bookings.length}):
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {results.created_bookings.slice(0, 10).map((booking_num, idx) => (
                      <Badge key={idx} className="bg-green-600/20 text-green-400 text-xs">
                        {booking_num}
                      </Badge>
                    ))}
                    {results.created_bookings.length > 10 && (
                      <Badge className="bg-gray-700 text-gray-400 text-xs">
                        +{results.created_bookings.length - 10} flere
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-700">
            <Button
              variant="outline"
              onClick={handleClose}
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
              disabled={loading}
            >
              {results ? 'Lukk' : 'Avbryt'}
            </Button>
            {!results && (
              <Button
                onClick={handleImport}
                disabled={!file || loading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {loading ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Importerer...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Importer Bookinger
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportModal;
